/**
 * REST client for the ephemeral signaling (`/api/p2p/*`).
 *
 * `fetchFn` is injectable for unit tests; on the web the platform's fetch is
 * used against the same origin (Vite proxy in dev / same host in prod).
 * The room token ALWAYS travels in the `x-p2p-token` header (never in the URL,
 * so it doesn't end up in logs).
 */

import { isRecord } from "./guards";
import type { TransferErrorKind } from "./transfer-machine";

export interface SignalingRoom {
  code: string;
  senderToken: string;
  ttlSecs: number;
}

export interface SignalingMessage {
  seq: number;
  payload: string;
}

export interface SignalingInbox {
  messages: SignalingMessage[];
  peerJoined: boolean;
}

export class SignalingError extends Error {
  constructor(
    public readonly kind: TransferErrorKind,
    public readonly status: number,
  ) {
    super(`Signaling request failed (${status}): ${kind}`);
    this.name = "SignalingError";
  }
}

function kindForStatus(status: number): TransferErrorKind {
  switch (status) {
    case 404: {
      return "expired";
    } // unknown or expired: same message so we don't leak which one
    case 409: {
      return "already-used";
    }
    case 400:
    case 401: {
      return "invalid-code";
    }
    default: {
      return "network";
    }
  }
}

export interface SignalingClientOptions {
  baseUrl?: string;
  fetchFn?: typeof fetch;
}

export interface SignalingClient {
  createRoom(ttlSecs?: number): Promise<SignalingRoom>;
  joinRoom(code: string): Promise<{ receiverToken: string }>;
  postMessage(code: string, token: string, payload: string): Promise<void>;
  fetchMessages(code: string, token: string, after: number): Promise<SignalingInbox>;
  closeRoom(code: string, token: string): Promise<void>;
}

export function createSignalingClient(options: SignalingClientOptions = {}): SignalingClient {
  const baseUrl = options.baseUrl ?? "";
  const fetchFn = options.fetchFn ?? fetch;

  // Returns `unknown` on purpose: each endpoint validates its shape with guards
  // (no type assertions over external JSON).
  async function request(
    path: string,
    init: RequestInit & { token?: string } = {},
  ): Promise<unknown> {
    const { token, ...rest } = init;
    let response: Response;
    try {
      response = await fetchFn(`${baseUrl}/api/p2p${path}`, {
        ...rest,
        headers: {
          "content-type": "application/json",
          ...(token ? { "x-p2p-token": token } : {}),
          ...rest.headers,
        },
      });
    } catch {
      throw new SignalingError("network", 0);
    }
    if (!response.ok) {
      throw new SignalingError(kindForStatus(response.status), response.status);
    }
    return (await response.json()) as unknown;
  }

  const malformed = () => new SignalingError("network", 0);

  return {
    async createRoom(ttlSecs) {
      const body = await request("/room", {
        method: "POST",
        body: JSON.stringify(ttlSecs ? { ttlSecs } : {}),
      });
      if (
        !isRecord(body) ||
        typeof body.code !== "string" ||
        typeof body.senderToken !== "string" ||
        typeof body.ttlSecs !== "number"
      ) {
        throw malformed();
      }
      return { code: body.code, senderToken: body.senderToken, ttlSecs: body.ttlSecs };
    },
    async joinRoom(code) {
      const body = await request(`/room/${encodeURIComponent(code)}/join`, {
        method: "POST",
        body: "{}",
      });
      if (!isRecord(body) || typeof body.receiverToken !== "string") {
        throw malformed();
      }
      return { receiverToken: body.receiverToken };
    },
    async postMessage(code, token, payload) {
      await request(`/room/${encodeURIComponent(code)}/message`, {
        method: "POST",
        token,
        body: JSON.stringify({ payload }),
      });
    },
    async fetchMessages(code, token, after) {
      const body = await request(
        `/room/${encodeURIComponent(code)}/messages?after=${encodeURIComponent(after)}`,
        { method: "GET", token },
      );
      if (
        !isRecord(body) ||
        !Array.isArray(body.messages) ||
        typeof body.peerJoined !== "boolean"
      ) {
        throw malformed();
      }
      const messages: SignalingMessage[] = [];
      const rawMessages: unknown[] = body.messages;
      for (const raw of rawMessages) {
        if (!isRecord(raw) || typeof raw.seq !== "number" || typeof raw.payload !== "string") {
          throw malformed();
        }
        messages.push({ seq: raw.seq, payload: raw.payload });
      }
      return { messages, peerJoined: body.peerJoined };
    },
    async closeRoom(code, token) {
      await request(`/room/${encodeURIComponent(code)}/close`, {
        method: "POST",
        token,
        body: "{}",
      });
    },
  };
}
